"use client";
import React, { useEffect, useState } from "react";
import { Skeleton } from "antd";
import { IconBuildingStore, IconArrowRight } from "@/lib/icons";
import Link from "next/link";
import SafeHtml from "../CMS/SafeHtml";
import { ENDPOINTS } from "@/constants/endpoint";
import AppImage, { getImageUrl } from "../common/AppImage";
import ORGANIZER_PLACEHOLDER from "@/images/organizer_placeholder.png";

export default function OrganizerInfoCard({ organizerSlug, initialData }) {
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData?.name);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setLoading(!initialData?.name);
    }
  }, [initialData]);

  useEffect(() => {
    // Skip API call if organizer data is already supplied from event details
    if (!organizerSlug || initialData?.name) return;

    let isCancelled = false;
    const fetchOrganizer = async () => {
      setLoading(true);
      try {
        const url = `${process.env.NEXT_PUBLIC_API_URL}/${ENDPOINTS.ORGANIZER_DETAIL.replace(/:slug|\{slug\}/g, organizerSlug)}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-Server-Secret":
              "H4KxWTUuPXWyhkxCq0AThQzDVrg7wRkIVeAuFim6uZJQJWDqvA8KWYKxqxhPejgY",
          },
        });
        const result = await response.json();
        if (!isCancelled && result?.data?.organizer) {
          setData(result.data.organizer);
        }
      } catch (err) {
        console.error("Failed to fetch organizer details", err);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    fetchOrganizer();

    return () => {
      isCancelled = true;
    };
  }, [organizerSlug, initialData?.name]);

  if (!organizerSlug && !data) return null;

  const logoSrc =
    getImageUrl(
      data?.feature_image ||
        data?.logo ||
        data?.profile_pic ||
        data?.image,
      { useUrl: true },
    ) || "";
  const profileSlug = data?.workspace_slug || organizerSlug;

  return (
    <div className="w-full rounded-2xl border border-[#E3E6E9] bg-white p-4 sm:p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03)] transition-all duration-200 hover:border-[#CBD5E1] hover:shadow-sm">
      <Skeleton
        loading={loading && !data?.name}
        avatar
        active
        paragraph={{ rows: 1 }}
      >
        {data ? (
          <div>
            {/* Card Header Row */}
            <div className="flex items-center justify-between gap-2 pb-2.5 mb-3 border-b border-[#F1F5F9]">
              <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-[#64748B] flex items-center gap-1.5">
                <IconBuildingStore size={14} className="text-[#0A9B8A]" />
                Organized by
              </span>
              <Link
                href={`/organizer/${profileSlug}`}
                prefetch={false}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A9B8A] hover:text-[#087F71] transition-colors group"
              >
                <span>View Profile</span>
                <IconArrowRight
                  size={13}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            </div>

            {/* Organizer Identity Row */}
            <div className="flex items-center gap-3">
              <Link
                href={`/organizer/${profileSlug}`}
                prefetch={false}
                className="relative w-12 h-12 rounded-xl overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC] shrink-0 flex items-center justify-center group hover:border-[#0A9B8A] transition-colors"
              >
                <AppImage
                  src={logoSrc || ORGANIZER_PLACEHOLDER}
                  fallbackSrc={ORGANIZER_PLACEHOLDER}
                  alt={data.name || "Organizer"}
                  fill
                  className="object-cover"
                  sizes="48px"
                />
              </Link>

              <div className="min-w-0 flex-1">
                <Link
                  href={`/organizer/${profileSlug}`}
                  prefetch={false}
                  className="block text-[#021526] hover:text-[#0A9B8A] transition-colors"
                >
                  <h3 className="m-0 text-base font-bold leading-tight truncate">
                    {data.name}
                  </h3>
                </Link>
              </div>
            </div>

            {/* Description or small common description */}
            <div className="mt-3 pt-2.5 border-t border-[#F1F5F9] text-xs text-[#64748B] line-clamp-2 leading-relaxed">
              {data.description &&
              typeof data.description === "string" &&
              data.description.trim().length > 0 ? (
                <SafeHtml html={data.description} />
              ) : (
                <span>
                  Bringing you the best events and experiences. Check out their profile for more details!
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center text-xs text-[#64748B] py-2">
            Organizer details not available.
          </div>
        )}
      </Skeleton>
    </div>
  );
}
